import { Box } from "@mui/joy";
import HighlightIndicator, {
  HighlightIndicatorWithNumber,
  CompletedHighlightIndicator,
  HighlightIndicatorWithQ,
} from "./HighlightIndicator";
import { ProgramContentType } from "data/BlockComponent";
import { isBlockUserFieldFilled } from "logic/logics";
import { motion } from "framer-motion";
import { useRecoilState, useRecoilValue } from "recoil";
import { permanentDataAtom } from "recoil/permanentDataAtom";
import { userAtom } from "../../../../recoil/userAtom";

export default function ProgressBarHighlight({
  taskKey,
  data,
  highlightIndexList,
  highlightColor,
  highlightGradientColor,
  showNumberIndicator,
  onClick,
}: {
  taskKey: string;
  data?: ProgramContentType[];
  highlightIndexList: number[];
  highlightColor?: string;
  highlightGradientColor?: string;
  showNumberIndicator?: boolean;
  onClick: (highlightIndex: number) => void;
}) {
  const [permanentDataState, setPermanentDataState] =
    useRecoilState(permanentDataAtom);
  const userState = useRecoilValue(userAtom)
  const isCoach = userState?.role !== "patient";

  const highlightWidth = data && data.length > 0 ? `${100 / data.length}%` : "0%";

  function getPercentage(index: number) {
    if (data) {
      return (index / data.length) * 100;
    }
    return 0;
  }

  const showIndicatorAnimation =
    !permanentDataState?.isHighlghtIndicatorClicked;

  return (
    <>
      {highlightIndexList.map((highlightIndex, orderIndex) => (
        <div key={`${taskKey}_highlight_click_${highlightIndex}`}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              left: `${getPercentage(highlightIndex)}%`,
              width: highlightWidth,
              height: "16px",
              backgroundColor: "transparent",
              // Ensure lower numbers appear on top when overlapping (patients only)
              zIndex: showNumberIndicator ? 1000 + (highlightIndexList.length - orderIndex) : "auto",
            }}
            onClick={() => {
              onClick(highlightIndex);
              setPermanentDataState((state) => ({
                ...state,
                isHighlghtIndicatorClicked: true,
              }));
            }}
          >
            {(() => {
              const hasAnswer = !!(data && data[highlightIndex] && isBlockUserFieldFilled(data[highlightIndex]));
              const shouldShowBase = !!showNumberIndicator;
              if (!shouldShowBase) return null;

              // Coach: show Q for new comments; show check when answered
              if (isCoach) {
                return (
                  <motion.div
                    style={{
                      position: "absolute",
                      transform: "translateX(-50%)",
                      left: "50%",
                      cursor: "pointer",
                    }}
                    initial={{ top: "10px" }}
                    animate={{
                      top: "7px",
                    }}
                    transition={{
                      duration: showIndicatorAnimation ? 0.5 : 0,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  >
                    {hasAnswer ? (
                      <CompletedHighlightIndicator />
                    ) : (
                      <HighlightIndicatorWithQ />
                    )}
                  </motion.div>
                );
              }

              // Patient: show stable numbering; replace with check when answered
              const stableNumber = orderIndex + 1;
              return (
                <motion.div
                  style={{
                    position: "absolute",
                    transform: "translateX(-50%)",
                    left: "50%",
                    cursor: "pointer",
                  }}
                  initial={{ top: "10px" }}
                  animate={{
                    top: "7px",
                  }}
                  transition={{
                    duration: showIndicatorAnimation ? 0.5 : 0,
                    repeat: Infinity,
                    repeatType: "reverse",
                  }}
                >
                  {hasAnswer ? (
                    <CompletedHighlightIndicator />
                  ) : (
                    <HighlightIndicatorWithNumber number={stableNumber} />
                  )}
                </motion.div>
              );
            })()}
          </Box>

          <Box
            sx={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              left: `${getPercentage(highlightIndex)}%`,
              width: highlightWidth,
              height: "100%",
              ...(highlightColor && { backgroundColor: highlightColor }),
              ...(highlightGradientColor && {
                background: highlightGradientColor,
              }),
              pointerEvents: "none",
            }}
          ></Box>
        </div>
      ))}
    </>
  );
}
